// NPV calculation function using proper year-by-year discounting
import {InvestmentBreakdown} from "@shared/interfaces/investmentBreakdown.interface.ts";

export function calculateNPV(annualSavings: number, initialInvestment: number, discountRate: number, years: number, ongoingSoftwareCost: number = 0): number {
    let npv = -initialInvestment;
    for (let year = 1; year <= years; year++) {
        npv += annualSavings / Math.pow(1 + discountRate, year);
    }
    return npv;
}

// Calculate financial projection data for Years 1–5.
export function calculateFinancialProjection(
    investmentBreakdown: InvestmentBreakdown,
    annualSavings: number,
    yearlyInvestmentOverrides?: number[],
    yearlyRampPercentages?: number[]
) {
    if (!annualSavings) return []

    const projectionData = []
    let cumulativeValue = 0

    const normalizedOverrides = Array.isArray(yearlyInvestmentOverrides)
        ? Array.from({ length: 5 }, (_, idx) => Number(yearlyInvestmentOverrides[idx] ?? 0) || 0)
        : null;

    const normalizedRamp = Array.isArray(yearlyRampPercentages)
        ? Array.from({ length: 5 }, (_, idx) => {
            const raw = Number(yearlyRampPercentages[idx] ?? 100);
            return Math.max(0, Math.min(100, Number.isFinite(raw) ? raw : 100));
        })
        : Array(5).fill(100);

    // If Year 5 is missing but earlier years are populated, carry forward the last known investment
    const filledOverrides = normalizedOverrides ? (() => {
        const copy = [...normalizedOverrides];
        if (copy[4] === 0 && copy.slice(0, 4).some(v => v > 0)) {
            copy[4] = copy[3] || copy.findLast((v) => v > 0) || 0;
        }
        return copy;
    })() : null;

    const yearlyInvestments = filledOverrides && filledOverrides.length === 5
        ? filledOverrides
        : [
            investmentBreakdown.year1Investment,
            ...Array(4).fill(0)
        ];

    for (let year = 1; year <= 5; year++) {
        const yearIndex = year - 1;
        const investment = yearlyInvestments[yearIndex] || 0
        const rampMultiplier = normalizedRamp[yearIndex] / 100;
        const savings = annualSavings * rampMultiplier
        const netBenefit = savings - investment

        cumulativeValue += netBenefit

        let investmentDetails = null
        if (year === 1) {
            investmentDetails = {
                platform: investmentBreakdown.platform,
                pillars: investmentBreakdown.totalPillars,
                entities: investmentBreakdown.discountedEntityCosts,
                setup: investmentBreakdown.year1SetupCosts,
                total: investment,
                description: "Platform + Pillars + Entity subscription + Setup costs"
            }
        } else {
            investmentDetails = {
                platform: 0,
                pillars: 0,
                entities: 0,
                total: 0,
                description: "No additional investment"
            }
        }

        projectionData.push({
            year: `Year ${year}`,
            yearNumber: year,
            investment: investment,
            savings: savings,
            netBenefit: netBenefit,
            cumulativeValue: cumulativeValue,
            rampPercent: normalizedRamp[yearIndex],
            investmentDetails: investmentDetails
        })
    }

    return projectionData
}

/**
 * Calculate IRR using Newton-Raphson method.
 * @param cashFlows Array of cash flows (first value is usually negative for investment).
 * @param guess Initial guess for IRR (default 0.1 = 10%).
 * @param maxIterations Maximum iterations to try.
 * @param tolerance Acceptable error margin for convergence.
 * @returns IRR as a decimal (e.g., 0.15 for 15%), or null if not found.
 */
export function calculateIRR(
    cashFlows: number[],
    guess = 0.1,
    maxIterations = 1000,
    tolerance = 1e-7
): number | null {
    let rate = guess;

    for (let i = 0; i < maxIterations; i++) {
        let npv = 0;
        let derivative = 0;

        for (let t = 0; t < cashFlows.length; t++) {
            npv += cashFlows[t] / Math.pow(1 + rate, t);
            derivative -= (t * cashFlows[t]) / Math.pow(1 + rate, t + 1);
        }

        const newRate = rate - npv / derivative;

        if (Math.abs(newRate - rate) < tolerance) {
            return newRate;
        }

        rate = newRate;
    }

    return null; // no convergence
}


/**
 * Calculate Internal Rate of Return (IRR) as a percentage.
 *
 * Uses the Newton–Raphson method to find the discount rate
 * that makes the Net Present Value (NPV) of the given cash flows equal to zero.
 *
 * @param cashFlows - An array of numbers representing the series of cash flows.
 *   The first value is usually negative (initial investment), followed by positive or negative returns.
 *   Example: [-1000, 300, 400, 500]
 * @param guess - Initial guess for IRR as a decimal (e.g., 0.1 for 10%). Defaults to `0.1`.
 * @param maxIterations - Maximum iterations for the Newton–Raphson method. Defaults to `1000`.
 * @param tolerance - Acceptable error margin for convergence. Defaults to `1e-7`.
 * @param decimals - Number of decimal places to round the percentage result to. Defaults to `0`.
 *
 * @returns IRR as a percentage (e.g., `15` for 15%), or `null` if convergence is not reached.
 *
 * @example
 * ```ts
 * const irr = calculateIRRPercent([-3675000, 3202500, 3202500, 3202500, 3202500, 3202500]);
 * console.log(irr); // 83
 * ```
 */
export function calculateIRRPercent(
    cashFlows: number[],
    guess = 0.1,
    maxIterations = 1000,
    tolerance = 1e-7,
    decimals = 0
): number | null {
    const r = calculateIRR(cashFlows, guess, maxIterations, tolerance);
    return r === null ? null : Number((r * 100).toFixed(decimals));
}
/**
 * Calculate Net Present Value (NPV) for a series of cash flows.
 * cashFlows[0] is treated as Year 1 (no discount), cashFlows[1] is Year 2, etc.
 *
 * Example (matches your sheet):
 *   const npv = getNPV(0.08, [
 *     3_202_500,               // Year 1
 *     3_202_500,               // Year 2
 *     3_202_500,               // Year 3
 *     3_202_500,               // Year 4
 *     3_202_500                // Year 5
 *   ]);
 *   // ≈ 9_111_653.89
 */
export function getNPV(discountRate: number, cashFlows: number[]): number {
    if (!Number.isFinite(discountRate)) throw new Error("discountRate must be a finite number");
    if (!Array.isArray(cashFlows) || cashFlows.length === 0) throw new Error("cashFlows must be a non-empty array");

    return cashFlows.reduce((pv: number, cf: number, t: number) => {
        if (!Number.isFinite(cf)) throw new Error(`cashFlows[${t}] must be a finite number`);
        return pv + cf / Math.pow(1 + discountRate, t);
    }, 0);
}

/**
 * Calculate ROI (Return on Investment) as a rounded percentage.
 * ROI = (Total Savings - Cost of Investment) / Cost of Investment * 100
 *
 * @param projectionData Array of projection objects with `yearNumber` and `netBenefit`.
 * @returns ROI percentage as an integer (e.g., 550 for 550%).
 */
export function getROI(projectionData: { yearNumber: number; netBenefit: number; investment?: number; savings?: number }[]): number {
    if (!projectionData || projectionData.length === 0) return 0;

    const totalSavings = projectionData.reduce((sum, y) => sum + (Number(y.savings) || 0), 0);
    const totalCosts = projectionData.reduce((sum, y) => sum + (Number(y.investment) || 0), 0);

    if (totalCosts === 0) return 0;

    const netCash = totalSavings - totalCosts;
    const roi = (netCash / totalCosts) * 100;
    return Math.round(roi);
}

// Excel-style payback: use Period 0–1 (years 0 and 1) costs and savings, convert to months
export function getPaybackMonths(
    projectionData: { yearNumber: number; investment?: number; savings?: number }[],
    periods: number = 2 // default: first two periods (0 and 1)
): number {
    if (!projectionData || projectionData.length === 0) return 0;

    const slice = projectionData.slice(0, periods);
    const totalSavings = slice.reduce((sum, y) => sum + (Number(y.savings) || 0), 0);
    const totalCosts = slice.reduce((sum, y) => sum + (Number(y.investment) || 0), 0);

    if (totalSavings === 0 || totalCosts === 0) return 0;

    const paybackMonths = (totalCosts / totalSavings) * 12;
    return Number(paybackMonths.toFixed(1));
}


export function extractTaskFTEAllocation(summary: any) {
    // Prefer new shape
    const tfa = summary?.taskFTEAllocation;
    if (tfa?.fteMatrix) return tfa;

    // Fallback: rebuild from legacy regions shape if present
    const legacy = summary?.fteAllocation?.fteAllocation ?? summary?.fteAllocation;
    if (legacy?.regions?.length) {
        const fteMatrix: Record<string, Record<string, number>> = {};
        const totalFTEsPerRegion: Record<string, number> = {};
        legacy.regions.forEach((r: any) => {
            totalFTEsPerRegion[r.regionName] = r.totalFTEs || 0;
            Object.entries(r.activityBreakdown || {}).forEach(([activity, ftes]) => {
                if (!fteMatrix[activity]) fteMatrix[activity] = {};
                fteMatrix[activity][r.regionName] = Number(ftes) || 0;
            });
        });
        return {
            fteMatrix,
            totalFTEsPerRegion,
            regionalTotals: {},                // can recompute if needed
            fteCostsByRegion: {},
            customActivities: [],
            automationPercentages: {}
        };
    }
    return undefined;
}
